import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { Switch } from '@/components/atoms/switch';
import { Badge } from '@/components/atoms/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  BookableServiceLineItemsEditor,
  type EditableQuoteLine,
} from '@/components/booking/BookableServiceLineItemsEditor';
import { useActiveJobTypes } from '@/hooks/settings/useJobTypes';
import { useFormTemplates } from '@/hooks/forms-engine/useFormTemplates';
import { useTerritories } from '@/hooks/territories/useTerritories';
import {
  SalesLineItemsReadOnlyTable,
  type SalesLineItemReadOnlyRow,
} from '@/components/organisms/sales/SalesLineItemsReadOnlyTable';
import {
  useBookableServices,
  useCreateBookableService,
  useDeleteBookableService,
  useUpdateBookableService,
} from '@/hooks/bookingConfig/useBookableServices';
import type {
  BookableService,
  BookingDepositMode,
  BookingDepositValueType,
  CreateBookableServiceDto,
} from '@/types/bookingConfig';

// Folded into the unified Online Booking section; keep the path as a redirect
// so old links/bookmarks land on the Services tab's new home.
export const Route = createFileRoute('/_dashboard/dashboard/settings/booking-services')({
  beforeLoad: () => {
    throw redirect({
      to: '/dashboard/settings/online-booking',
      search: { tab: 'services' },
    });
  },
});

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Bookable-services management. `embedded` drops the standalone page chrome
 * (outer padding + title) so it can live inside the unified Online Booking
 * tabs; standalone it keeps its own header.
 */
export function BookableServicesPanel({ embedded = false }: { embedded?: boolean }) {
  const { data: services = [], isLoading } = useBookableServices();
  const remove = useDeleteBookableService();
  const [editing, setEditing] = useState<BookableService | null>(null);
  const [open, setOpen] = useState(false);

  const openNew = () => {
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (svc: BookableService) => {
    setEditing(svc);
    setOpen(true);
  };

  return (
    <div className={embedded ? 'space-y-4' : 'mx-auto max-w-4xl space-y-6 p-6'}>
      <header className="flex items-center justify-between">
        <div>
          {!embedded && <h1 className="text-xl font-semibold">Bookable Services</h1>}
          <p className="text-sm text-muted-foreground">
            Services customers can pick on your online booking links.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> New service
        </Button>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : services.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No bookable services yet. Create one to start taking online bookings.
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {services.map((svc) => (
            <li key={svc.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{svc.name}</span>
                  {!svc.isActive && <Badge variant="secondary">Inactive</Badge>}
                  {svc.depositMode === 'REQUIRED' && <Badge variant="outline">Deposit</Badge>}
                </div>
                <div className="truncate text-sm text-muted-foreground">
                  {money(svc.priceCents)}
                  {svc.jobType ? ` · ${svc.jobType.name}` : ''}
                  {svc.category ? ` · ${svc.category}` : ''}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(svc)} aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (confirm(`Remove "${svc.name}"?`)) remove.mutate(svc.id);
                  }}
                  aria-label="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ServiceFormDialog open={open} service={editing} onClose={() => setOpen(false)} />
    </div>
  );
}

interface FormState {
  jobTypeId: string;
  name: string;
  priceDollars: string;
  category: string;
  imageUrl: string;
  description: string;
  depositMode: BookingDepositMode;
  depositType: BookingDepositValueType;
  depositValue: string;
  isActive: boolean;
  formDefinitionId: string;
  territoryIds: string[];
}

const emptyForm: FormState = {
  jobTypeId: '',
  name: '',
  priceDollars: '',
  category: '',
  imageUrl: '',
  description: '',
  depositMode: 'NONE',
  depositType: 'PERCENT',
  depositValue: '',
  isActive: true,
  formDefinitionId: '',
  territoryIds: [],
};

/** A job type's price is the sum of its selected pricebook line items, using
 *  each row's persisted engine total (base price × quantity only as a fallback)
 *  — mirrors the job-type list summary and the server's booking-price fallback. */
function jobTypeLineItemsTotal(jobType: { lineItems?: Array<Record<string, unknown>> }): number {
  let total = 0;
  for (const raw of jobType.lineItems ?? []) {
    if (raw.isSelected === false) continue;
    // Trust the persisted, engine-computed lineTotal when present (covers T&M /
    // cost-plus / discounted rows) — even $0 for a fully discounted line. Only
    // reconstruct basePrice * quantity for legacy rows with no stored total.
    if (raw.lineTotal != null) {
      const lineTotal = Number(raw.lineTotal);
      if (Number.isFinite(lineTotal)) {
        total += lineTotal;
        continue;
      }
    }
    const basePrice = Number(raw.basePrice ?? 0);
    const quantity = Number(raw.quantity ?? 1);
    if (Number.isFinite(basePrice) && Number.isFinite(quantity)) {
      total += basePrice * quantity;
    }
  }
  return total;
}

/** Whether a job type defines its price via (selected) line items at all. A
 *  type with selected rows is authoritative even at a $0 total; one with none
 *  leaves the price blank so the server uses the legacy default. */
function jobTypeHasLineItemPricing(jobType: {
  lineItems?: Array<Record<string, unknown>>;
}): boolean {
  return (jobType.lineItems ?? []).some((raw) => raw.isSelected !== false);
}

/** Resolve the price (in cents) a blank field defaults to, mirroring the server:
 *  the selected line-items total when the type has line-item pricing (including
 *  $0), otherwise the legacy defaultPrice. Sent explicitly so a cleared price
 *  updates an existing service too — omitting it would leave the stale value. */
function jobTypeFallbackPriceCents(jobType: {
  lineItems?: Array<Record<string, unknown>>;
  defaultPrice?: number | null;
}): number {
  if (jobTypeHasLineItemPricing(jobType)) {
    return Math.round(jobTypeLineItemsTotal(jobType) * 100);
  }
  return jobType.defaultPrice != null ? Math.round(jobType.defaultPrice * 100) : 0;
}

// Sentinel for the "no survey" option — the Select primitive can't use an empty
// string value, so map it to null on submit.
const NO_SURVEY = 'none';

export function ServiceFormDialog({
  open,
  service,
  onClose,
}: {
  open: boolean;
  service: BookableService | null;
  onClose: () => void;
}) {
  const { data: activeJobTypes = [] } = useActiveJobTypes();
  // Only booking surveys are attachable to a bookable service.
  const { data: formDefinitions = [] } = useFormTemplates({
    surface: 'BOOKING',
    status: 'ACTIVE',
  });
  const { data: allTerritories = [] } = useTerritories();
  // The public booking flow resolves coverage from the customer's ZIP only, so
  // only active ZIP-based territories can gate a service. Polygon/radius/
  // archived territories aren't offered (they'd never match a zip-only lookup).
  const territories = allTerritories.filter(
    (t) => t.status === 'ACTIVE' && t.boundaryType === 'ZIP_LIST',
  );
  const create = useCreateBookableService();
  const update = useUpdateBookableService();
  const [form, setForm] = useState<FormState>(emptyForm);
  // Quoted pricebook lines (the wizard prices them live with tax); display
  // metadata is hydrated by the editor, only the refs are persisted.
  const [pricebookRows, setPricebookRows] = useState<EditableQuoteLine[]>([]);
  // The price value the dialog opened with — an untouched field on a
  // pricebook-quoted service means "keep deriving from the lines", not "pin
  // the old snapshot", so submit treats it as blank in that case.
  const initialPriceDollars = useRef<string>('');

  useEffect(() => {
    if (!open) return;
    initialPriceDollars.current = service ? (service.priceCents / 100).toString() : '';
    setForm(
      service
        ? {
            jobTypeId: service.jobTypeId,
            name: service.name,
            priceDollars: (service.priceCents / 100).toString(),
            category: service.category ?? '',
            imageUrl: service.imageUrl ?? '',
            description: service.description ?? '',
            depositMode: service.depositMode,
            depositType: service.depositType,
            depositValue: service.depositValue ?? '',
            isActive: service.isActive,
            formDefinitionId: service.formDefinitionId ?? '',
            territoryIds: service.territories?.map((t) => t.territoryId) ?? [],
          }
        : emptyForm,
    );
    setPricebookRows(
      (service?.pricebookItems ?? []).map((item) => ({
        lineItemId: item.lineItemId,
        quantity: item.quantity,
        isOptional: item.isOptional === true,
        // Carried, not edited: no control writes it, and rebuilding the ref
        // without it would drop a stored answer on the next save.
        pricingTreatment: item.pricingTreatment,
      })),
    );
  }, [open, service]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Picking a job type suggests its line-items total as the manual price, so the
  // admin SEES the price the server would apply (and can override). Only fills
  // when the field is blank or still holds the previous type's auto-suggestion —
  // never clobbers a hand-typed price.
  const handleJobTypeChange = (nextId: string) =>
    setForm((f) => {
      const prev = activeJobTypes.find((j) => j.id === f.jobTypeId);
      const next = activeJobTypes.find((j) => j.id === nextId);
      const prevHasPricing = prev ? jobTypeHasLineItemPricing(prev) : false;
      const nextHasPricing = next ? jobTypeHasLineItemPricing(next) : false;
      const prevTotal = prev ? jobTypeLineItemsTotal(prev) : 0;
      const nextTotal = next ? jobTypeLineItemsTotal(next) : 0;
      // The price is "auto" if it's blank or still equals the previous type's
      // line-item total (including a $0 total). Compare in cents, not strings —
      // the edit form loads the value as e.g. "100" (not "100.00"), so a string
      // match would misread an untouched price as a manual override. Only when
      // it's auto do we overwrite it.
      const fieldCents =
        f.priceDollars.trim() === '' ? null : Math.round(Number(f.priceDollars) * 100);
      const priceWasAuto =
        fieldCents === null || (prevHasPricing && fieldCents === Math.round(prevTotal * 100));
      return {
        ...f,
        jobTypeId: nextId,
        // Switching to a type with line-item pricing fills its total (even
        // $0 for a free service); one without leaves the field blank so the
        // admin sets it and the server falls back to the legacy default.
        priceDollars: priceWasAuto ? (nextHasPricing ? nextTotal.toFixed(2) : '') : f.priceDollars,
      };
    });

  // Read-only breakdown of the selected job type's line items — the source of
  // its price, shown so the admin sees what the (pre-filled) price is made of.
  const selectedJobType = activeJobTypes.find((j) => j.id === form.jobTypeId);
  const jobTypeLineItemRows: SalesLineItemReadOnlyRow[] = useMemo(
    () =>
      (selectedJobType?.lineItems ?? [])
        .filter((li) => li.isSelected !== false)
        .map((li, i) => {
          const qty = Number(li.quantity ?? 1) || 1;
          const unitPrice = Number(li.basePrice ?? 0) || 0;
          // Trust the persisted engine total when present (T&M / cost-plus /
          // discounted) — even $0; fall back to unitPrice * qty only for legacy
          // rows with no stored total.
          const stored = li.lineTotal != null ? Number(li.lineTotal) : NaN;
          const total = Number.isFinite(stored) ? stored : unitPrice * qty;
          return {
            id: String(li.lineItemId ?? i),
            name: String(li.name ?? 'Item'),
            code: li.code ? String(li.code) : null,
            type: String(li.type ?? 'SERVICE'),
            quantity: qty,
            unit: li.unit ? String(li.unit) : null,
            unitPrice,
            total,
          };
        }),
    [selectedJobType],
  );

  const jobTypeOptions = useMemo(() => {
    const selectedIsActive = activeJobTypes.some((jobType) => jobType.id === form.jobTypeId);
    if (!service || !form.jobTypeId || selectedIsActive) {
      return activeJobTypes.map((jobType) => ({ ...jobType, disabled: false }));
    }

    return [
      {
        id: form.jobTypeId,
        name: `${service.jobType?.name ?? 'Archived job type'} (Archived)`,
        disabled: true,
      },
      ...activeJobTypes.map((jobType) => ({ ...jobType, disabled: false })),
    ];
  }, [activeJobTypes, form.jobTypeId, service]);

  const submit = () => {
    const dto: CreateBookableServiceDto = {
      jobTypeId: form.jobTypeId,
      name: form.name.trim(),
      // A blank price resolves to the job type's fallback (line-items total or
      // legacy default) and is sent explicitly — including on edit, where
      // omitting it would keep the previously persisted price. When the selected
      // job type isn't loaded (e.g. archived on edit), omit it to preserve the
      // existing value rather than overwrite it with a guessed $0.
      priceCents:
        // With quoted pricebook lines, a blank OR untouched price means "let
        // the server derive from the required lines" — otherwise editing a
        // service's lines would silently pin the stale card price.
        pricebookRows.length > 0
          ? form.priceDollars.trim() === '' || form.priceDollars === initialPriceDollars.current
            ? undefined
            : Math.round(Number(form.priceDollars || '0') * 100)
          : form.priceDollars.trim() === ''
            ? selectedJobType
              ? jobTypeFallbackPriceCents(selectedJobType)
              : undefined
            : Math.round(Number(form.priceDollars || '0') * 100),
      // The price is job-type/manual now; unlink any legacy single pricebook item.
      pricebookLineItemId: null,
      // Persist only the refs — pricing is resolved server-side at quote time.
      pricebookItems: pricebookRows.map((row) => ({
        lineItemId: row.lineItemId,
        quantity: row.quantity,
        isOptional: row.isOptional === true,
        // Sent back exactly as it was loaded — see the row mapping above.
        ...(row.pricingTreatment && { pricingTreatment: row.pricingTreatment }),
      })),
      category: form.category.trim() || undefined,
      imageUrl: form.imageUrl.trim() || undefined,
      description: form.description.trim() || undefined,
      depositMode: form.depositMode,
      depositType: form.depositType,
      depositValue: form.depositValue ? Number(form.depositValue) : undefined,
      isActive: form.isActive,
      formDefinitionId: form.formDefinitionId || null,
      territoryIds: form.territoryIds,
    };
    const onDone = { onSuccess: onClose };
    if (service) {
      update.mutate({ id: service.id, dto }, onDone);
    } else {
      create.mutate(dto, onDone);
    }
  };

  const toggleTerritory = (id: string) =>
    setForm((f) => ({
      ...f,
      territoryIds: f.territoryIds.includes(id)
        ? f.territoryIds.filter((t) => t !== id)
        : [...f.territoryIds, id],
    }));

  // Price is optional — a blank one defaults to the job type's line-items total.
  const valid = form.jobTypeId && form.name.trim().length > 0;
  const saving = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{service ? 'Edit service' : 'New bookable service'}</DialogTitle>
          <DialogDescription>
            Maps to a job type (duration + skills) and adds the customer-facing price and deposit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="bs-name">Name</Label>
            <Input
              id="bs-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Standard AC Tune-Up"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Job type</Label>
            <Select value={form.jobTypeId} onValueChange={handleJobTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a job type" />
              </SelectTrigger>
              <SelectContent>
                {jobTypeOptions.map((jt) => (
                  <SelectItem key={jt.id} value={jt.id} disabled={jt.disabled}>
                    {jt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Intake survey (optional)</Label>
            <Select
              value={form.formDefinitionId || NO_SURVEY}
              onValueChange={(v) => set('formDefinitionId', v === NO_SURVEY ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="No survey" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SURVEY}>No survey</SelectItem>
                {formDefinitions.map((fd) => (
                  <SelectItem key={fd.id} value={fd.id}>
                    {fd.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Customers answer these questions in the booking wizard for this service.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Quoted pricebook lines (optional)</Label>
            <BookableServiceLineItemsEditor value={pricebookRows} onChange={setPricebookRows} />
            {pricebookRows.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Add pricebook items to show customers an itemized quote with tax for their address
                in the booking wizard.
              </p>
            )}
          </div>

          {jobTypeLineItemRows.length > 0 && pricebookRows.length === 0 && (
            <div className="space-y-1.5">
              <Label>Line items · from {selectedJobType?.name}</Label>
              <SalesLineItemsReadOnlyTable items={jobTypeLineItemRows} />
              <p className="text-xs text-muted-foreground">
                This job type&apos;s price. The price below defaults to their total — override it
                only if this service should cost something different.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="bs-price">Price</Label>
            <Input
              id="bs-price"
              type="number"
              min="0"
              step="0.01"
              value={form.priceDollars}
              onChange={(e) => set('priceDollars', e.target.value)}
              placeholder="149.00"
            />
            <p className="text-xs text-muted-foreground">
              {pricebookRows.length > 0
                ? 'Defaults to the quoted pricebook lines’ required total. Override only if the card should show something different.'
                : jobTypeLineItemRows.length > 0
                  ? 'Defaults to the job type line-items total above. Override only if this service should cost something different.'
                  : 'The price customers see for this service.'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bs-category">Category</Label>
            <Input
              id="bs-category"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              placeholder="Maintenance"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bs-image">Image URL</Label>
            <Input
              id="bs-image"
              value={form.imageUrl}
              onChange={(e) => set('imageUrl', e.target.value)}
              placeholder="https://…"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bs-desc">Description</Label>
            <Textarea
              id="bs-desc"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Deposit</Label>
              <Select
                value={form.depositMode}
                onValueChange={(v) => set('depositMode', v as BookingDepositMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">No deposit</SelectItem>
                  <SelectItem value="REQUIRED">Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.depositMode === 'REQUIRED' && (
              <div className="space-y-1.5">
                <Label>Deposit amount</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.depositValue}
                    onChange={(e) => set('depositValue', e.target.value)}
                    placeholder={form.depositType === 'PERCENT' ? '25' : '50.00'}
                  />
                  <Select
                    value={form.depositType}
                    onValueChange={(v) => set('depositType', v as BookingDepositValueType)}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENT">%</SelectItem>
                      <SelectItem value="FLAT">$ flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Service areas (optional)</Label>
            {territories.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No ZIP-based territories available. This service is offered wherever a technician
                covers.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {territories.map((t) => {
                    const selected = form.territoryIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleTerritory(t.id)}
                        className={`rounded-full border px-3 py-1 text-sm transition ${
                          selected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'hover:border-primary'
                        }`}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {form.territoryIds.length === 0
                    ? 'None selected — available wherever a technician covers.'
                    : 'Only offered in the selected territories.'}
                </p>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              id="bs-active"
            />
            <Label htmlFor="bs-active">Active (visible on links)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving…' : service ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
